/**
 * Animation Loop
 * 
 * Main animation loop using requestAnimationFrame.
 * Handles interpolation, DOM updates, and rendering.
 */

// Square animation removed - elements don't have animation state
// Animation profiles and interpolators kept for potential future use
// import { getAnimationProfile } from './profiles.js';
// import { spring, springValue } from './interpolators.js';
import type { AnimationProfileType } from '../types/enums.js';

/**
 * Animation loop state
 */
export interface AnimationLoopState {
  active: boolean;
  rafId: number | null;
  fpsFrames: number;
  fpsLastTime: number;
  fps: number;
  needsRedraw: boolean;
}

/**
 * Create a new animation loop state
 */
function createAnimationLoopState(): AnimationLoopState {
  return {
    active: false,
    rafId: null,
    fpsFrames: 0,
    fpsLastTime: performance.now(),
    fps: 0,
    needsRedraw: false,
  };
}

/**
 * Animation tick callback type
 */
export type TickCallback = () => void;

/**
 * Animation loop manager
 */
export class AnimationLoop {
  private state: AnimationLoopState;
  private onTick: TickCallback | null = null;

  constructor() {
    this.state = createAnimationLoopState();
  }

  /**
   * Set the tick callback
   */
  setTickCallback(callback: TickCallback): void {
    this.onTick = callback;
  }

  /**
   * Start the animation loop
   */
  start(): void {
    if (this.state.active) return;
    this.state.active = true;
    this.state.fpsLastTime = performance.now();
    this.state.fpsFrames = 0;
    this.tick();
  }

  /**
   * Stop the animation loop
   */
  stop(): void {
    if (!this.state.active) return;
    this.state.active = false;
    if (this.state.rafId !== null) {
      cancelAnimationFrame(this.state.rafId);
      this.state.rafId = null;
    }
  }

  /**
   * Check if animation is active
   */
  isActive(): boolean {
    return this.state.active;
  }

  /**
   * Mark that a redraw is needed
   */
  markRedraw(): void {
    this.state.needsRedraw = true;
  }

  /**
   * Clear redraw flag (after rendering)
   */
  clearRedraw(): void {
    this.state.needsRedraw = false;
  }

  /**
   * Get whether redraw is needed
   */
  getNeedsRedraw(): boolean {
    return this.state.needsRedraw;
  }

  /**
   * Get current FPS
   */
  getFPS(): number {
    return this.state.fps;
  }

  /**
   * Animation tick
   */
  private tick = (): void => {
    const now = performance.now();

    // Update FPS counter
    this.state.fpsFrames++;
    if (this.state.fpsFrames >= 10) {
      const elapsed = now - this.state.fpsLastTime;
      this.state.fps = Math.round((this.state.fpsFrames / elapsed) * 1000);
      this.state.fpsFrames = 0;
      this.state.fpsLastTime = now;

      // Update FPS display
      const fpsEl = document.getElementById('fpsInfo');
      if (fpsEl) {
        fpsEl.textContent = `${this.state.fps} fps`;
        if (this.state.fps >= 60) {
          fpsEl.style.color = '#6cf';
        } else if (this.state.fps >= 30) {
          fpsEl.style.color = '#fc6';
        } else {
          fpsEl.style.color = '#f66';
        }
      }
    }

    // Call tick callback
    if (this.onTick) {
      this.onTick();
    }

    // PERFORMANCE: Only continue loop if active AND something needs redrawing
    // This prevents unnecessary frame rendering when UI is static
    if (this.state.active) {
      if (this.state.needsRedraw) {
        // Continue loop if something needs redrawing
        this.state.rafId = requestAnimationFrame(this.tick);
      } else {
        // Stop loop if nothing needs redrawing
        this.state.active = false;
        if (this.state.rafId !== null) {
          cancelAnimationFrame(this.state.rafId);
          this.state.rafId = null;
        }
      }
    }
  };
}

// Square animation removed - elements are positioned directly by allocator
// Animation will be added back in the future if needed

